import { useEffect, useRef, useState } from 'react'
import { afterSocialConnect } from '@/lib/setupSteps'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'
import { LuInstagram as Instagram } from 'react-icons/lu'
import { handleInstagramCallback } from '@/lib/api'
import InstagramConnectFailed from '@/components/InstagramConnectFailed'
import InstagramConnectedCard, {
  type InstagramConnected,
} from '@/components/InstagramConnectedCard'
import { useAuth } from '@/hooks/useAuth'

const InstagramCallback = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser, athlete } = useAuth()
  // Read inside the one-time callback effect without re-running it (the code can only be used once)
  const athleteRef = useRef(athlete)
  athleteRef.current = athlete
  // What went wrong, in words for the athlete. Null message: show the general advice.
  const [failure, setFailure] = useState<{ cancelled: boolean; message: string | null } | null>(
    null,
  )
  const [connected, setConnected] = useState<InstagramConnected | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)
  // The code can only be used once, and React strict mode runs effects twice in development
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const processCallback = async () => {
      const urlParams = new URLSearchParams(location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const errorParam = urlParams.get('error')

      if (errorParam) {
        // Instagram's own wording is for developers; only the athlete's choice matters here
        setFailure({ cancelled: errorParam === 'access_denied', message: null })
        setIsProcessing(false)
        return
      }

      if (!code || !state) {
        setFailure({ cancelled: false, message: null })
        setIsProcessing(false)
        return
      }

      try {
        const result = await handleInstagramCallback(code, state)
        // Settings reads the connected accounts from the signed-in athlete, loaded before this
        await refreshUser()
        setConnected(result)
        setIsProcessing(false)
        // With everything approved there is nothing to read. When something was declined, stay
        // until the athlete chooses, so they cannot miss it.
        if (result.missing_permissions.length === 0) {
          setTimeout(() => navigate(afterSocialConnect(athleteRef.current)), 2500)
        }
      } catch (err) {
        console.error('Error connecting Instagram:', err)
        setFailure({ cancelled: false, message: err instanceof Error ? err.message : null })
        setIsProcessing(false)
      }
    }

    processCallback()
  }, [location.search, navigate, refreshUser])

  const handleRetry = () => {
    navigate('/connect/instagram')
  }

  const handleContinue = () => {
    navigate(afterSocialConnect(athleteRef.current))
  }

  const handleCancel = () => {
    navigate(afterSocialConnect(athlete))
  }

  if (connected) {
    return (
      <InstagramConnectedCard
        connected={connected}
        onContinue={handleContinue}
        onRetry={handleRetry}
      />
    )
  }

  if (failure) {
    return (
      <InstagramConnectFailed
        cancelled={failure.cancelled}
        message={failure.message}
        onRetry={handleRetry}
        onLeave={handleCancel}
      />
    )
  }

  // Waiting for Instagram's answer and our server's
  return (
    <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
      <Card variant="dark" className="overflow-hidden w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-linear-to-r from-[#FF3A89] to-[#FFC702] p-4 rounded-full">
              <Instagram className="h-8 w-8 text-[#FFFFFF]" />
            </div>
          </div>
          <CardTitle className="text-foreground text-xl">Connecting Instagram</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 text-[#4d7c0f] animate-spin" />
          <p className="text-gray-700">Finishing your Instagram connection...</p>
        </CardContent>
      </Card>
    </div>
  )
}

export default InstagramCallback
